<template>
    <!--
        What a guest sees when the hotel has not switched online check-in on.
        Deliberately NOT the dead-link screen: that one sends them to the desk for a
        new link, and a new link would open exactly the same refusal. Nothing is
        broken here and nothing expired — the hotel simply does not offer this — so
        the screen says so and points at the one place that can help.

        No button at all, of any kind. A guest can do nothing about a licence, and a
        retry would only ask a locked door the same question — so the one thing that
        does help is said as a plain sentence instead: a different link opens as a
        different guest, and this screen does not survive it. It wears the same card as
        the dead-link screen (the prototype's `.invalid-*` classes, colours from --n /
        --m / --o and their dark counterparts), so a guest meets one visual language
        whichever way a link does not open.
    -->
    <section class="invalid-state" aria-labelledby="license-title">
        <header v-if="hotelName" class="invalid-hotel">
            <div class="invalid-hotel-mark" aria-hidden="true">{{ monogram }}</div>
            <div>
                <strong>{{ hotelName }}</strong>
                <small>{{ $t('invalid.reception') }}</small>
            </div>
        </header>

        <div class="invalid-illustration" aria-hidden="true">
            <span class="invalid-link">⌂</span>
        </div>

        <p class="invalid-eyebrow">{{ $t('license.eyebrow') }}</p>
        <h1 id="license-title">{{ $t('license.title') }}</h1>
        <p class="invalid-lead">{{ $t('license.lead') }}</p>

        <p class="license-hint">{{ $t('license.another_link') }}</p>

        <p class="invalid-security">
            <span aria-hidden="true">◆</span>
            {{ $t('license.contact') }}
        </p>
    </section>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'

    import { useAppStore } from '@/stores/app'

    const appStore = useAppStore()

    /*
     * The house's name, only where this portal already holds it.
     *
     * Usually it does not: the stay is the very call the hotel's missing module
     * refuses, so there is no property to read a name from. The lockup is then left
     * off altogether rather than filled with "Your hotel" — a guest who has been told
     * nothing specific is better served by no header than by a placeholder that looks
     * like the hotel forgot its own name.
     */
    const hotelName = computed((): string =>
        appStore.stay?.property.portal.display_name || appStore.stay?.property.name || '')

    const monogram = computed((): string =>
        appStore.stay?.property.portal.monogram ||
        hotelName.value.replace(/^Hotel\s+/i, '').charAt(0).toUpperCase() ||
        'H')
</script>
